/**
 * Навигация внутри проекта.
 *
 * Слева, тёмной панелью: содержание проекта важнее навигации, поэтому панель
 * уходит на второй план и не спорит с полотном.
 *
 * Сверху — название проекта и готовность текущего этапа: главный ориентир
 * «где мы находимся» должен быть виден с любой страницы
 * (документ «Структура интерфейса», §3.2).
 */

import {
  ActivityIcon,
  BanknoteIcon,
  FlagIcon,
  LayoutDashboardIcon,
  ListIcon,
  PackageIcon,
  SearchIcon,
  SettingsIcon,
  UsersIcon,
  WrenchIcon,
} from 'lucide-react';
import { NavLink } from 'react-router';
import type { ProjectDto } from '@project-os/contracts';
import { PROJECT_STATUS_LABELS_RU } from '@project-os/contracts';
import { ProgressRing } from '@/components/common/progress-ring';
import { StatusBadge } from '@/components/common/status-badge';
import { ProjectLogo } from '@/components/common/project-logo';
import { useCommandPalette } from '@/components/layout/command-palette-context';
import { cn } from '@/lib/utils';

const SECTIONS = [
  { to: '', label: 'Обзор', icon: LayoutDashboardIcon, end: true },
  { to: 'items', label: 'Элементы', icon: ListIcon },
  { to: 'stages', label: 'Этапы', icon: FlagIcon },
  { to: 'assets', label: 'Активы', icon: PackageIcon },
  { to: 'resources', label: 'Ресурсы', icon: WrenchIcon },
  { to: 'finance', label: 'Финансы', icon: BanknoteIcon },
  { to: 'activity', label: 'История', icon: ActivityIcon },
  { to: 'members', label: 'Участники', icon: UsersIcon },
  { to: 'settings', label: 'Настройки', icon: SettingsIcon },
] as const;

const PROJECT_STATUS_TONES = {
  IDEA: 'neutral',
  ACTIVE: 'success',
  PAUSED: 'warning',
  COMPLETED: 'neutral',
} as const;

export function ProjectSidebar({
  project,
  onNavigate,
}: {
  project: ProjectDto;
  onNavigate?: () => void;
}) {
  const stage = project.currentStage;
  const palette = useCommandPalette();

  return (
    <nav className="flex h-full flex-col" aria-label="Разделы проекта">
      {/* Название и состояние проекта */}
      <div className="px-3 pt-3 pb-2">
        <div className="flex items-start gap-2">
          <ProjectLogo project={project} size="md" className="mt-px" />
          <h2 className="min-w-0 flex-1 text-sm leading-snug font-semibold text-sidebar-accent-foreground">
            {project.name}
          </h2>
          <StatusBadge
            label={PROJECT_STATUS_LABELS_RU[project.status]}
            tone={PROJECT_STATUS_TONES[project.status]}
          />
        </div>

        {stage ? (
          <div className="mt-3 flex items-center gap-2.5">
            {/* Кольцо готовности показывается, только когда есть чему быть
                готовым. Пустое кольцо рядом с «0 из 0» выглядит как ноль
                выполненного, хотя выполнять пока нечего. */}
            {stage.requirementsTotal > 0 ? (
              <ProgressRing
                value={stage.requirementsDone}
                max={stage.requirementsTotal}
                size={34}
                thickness={3}
              />
            ) : null}
            <div className="min-w-0">
              <p className="truncate text-xs font-medium text-sidebar-accent-foreground">
                {stage.name}
              </p>
              <p className="text-2xs text-sidebar-foreground/70 tabular-nums">
                {stage.requirementsTotal > 0
                  ? `${stage.requirementsDone} из ${stage.requirementsTotal} требований`
                  : 'Требования не заданы'}
              </p>
            </div>
          </div>
        ) : (
          <p className="mt-2 text-xs text-sidebar-foreground/60">Текущий этап не задан</p>
        )}
      </div>

      {/* Поиск и быстрые действия */}
      <div className="px-2 pb-1">
        <button
          type="button"
          onClick={() => palette.open()}
          className={cn(
            'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left',
            'text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
            'transition-colors',
          )}
        >
          <SearchIcon className="size-4 shrink-0" />
          <span className="flex-1 text-sm">Поиск и действия</span>
          <kbd className="rounded border border-sidebar-border px-1.5 py-0.5 text-2xs text-sidebar-foreground/60">
            Ctrl K
          </kbd>
        </button>
      </div>

      <ul className="flex-1 space-y-px overflow-y-auto p-2 pt-1">
        {SECTIONS.map((section) => (
          <li key={section.label}>
            <NavLink
              to={section.to ? `/projects/${project.id}/${section.to}` : `/projects/${project.id}`}
              end={'end' in section ? section.end : false}
              onClick={onNavigate}
              viewTransition
              className={({ isActive }) =>
                cn(
                  'group/nav relative flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm',
                  'transition-[background-color,color] duration-120 ease-out-soft',
                  'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                  isActive && 'bg-sidebar-accent font-medium text-sidebar-accent-foreground',
                )
              }
            >
              {({ isActive }) => (
                <>
                  {/* Индикатор один на весь список: одно и то же имя
                      view-transition-name на разных пунктах заставляет
                      браузер перенести полоску между ними, а не потушить
                      одну и зажечь другую. Там, где View Transitions нет
                      (Firefox, Safari), полоска просто появляется на новом
                      месте — навигация от этого не страдает. */}
                  {isActive ? (
                    <span
                      aria-hidden
                      className="absolute inset-y-1 left-0 w-0.5 rounded-full bg-sidebar-primary"
                      style={{ viewTransitionName: 'pos-nav-indicator' }}
                    />
                  ) : null}

                  <section.icon
                    className={cn(
                      'size-4 shrink-0 transition-opacity duration-120 ease-out-soft',
                      isActive ? 'opacity-100' : 'opacity-80 group-hover/nav:opacity-100',
                    )}
                  />
                  {section.label}
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
